import React from "react";
import { motion } from "framer-motion";
import {
  MessageCircle,
  ShoppingCart,
  ChefHat,
  Mail,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { openExternalLink } from "../../services/site/linkService";
import { InfoService } from "../../services/site/InfoService";
import "./BookingShowcase.scss";

const BookingShowcase: React.FC = () => {
  const navigate = useNavigate();

  const steps = [
    {
      n: "01",
      icon: <MessageCircle />,
      title: "Customer messages your WhatsApp",
      desc: "No app install. They just say 'menu' and the bot opens your catalogue with photos & prices.",
    },
    {
      n: "02",
      icon: <ShoppingCart />,
      title: "Bot guides them: menu → cart → pay",
      desc: "Combos, coupons, payment links — Razorpay / Stripe / Cashfree / COD. All on chat.",
    },
    {
      n: "03",
      icon: <ChefHat />,
      title: "Order lands in your KDS, stock auto-deducts",
      desc: "Recipe-linked inventory drops the right raw materials. Chef sees colour-coded urgency.",
    },
    {
      n: "04",
      icon: <Mail />,
      title: "Vendor invoice arrives → manager approves on WhatsApp",
      desc: "Gmail polls every 5 min. AI parses the PDF, flags overcharges, sends one-tap approval.",
    },
  ];

  const differentiators = [
    {
      icon: "💬",
      title: "WhatsApp-first",
      desc: "Customers don't install apps. Managers approve from chat. Zero learning curve.",
    },
    {
      icon: "🤖",
      title: "AI-native",
      desc: "Invoice parsing, recipe suggestions, report chat, HSN codes — built in, not bolted on.",
    },
    {
      icon: "🏭",
      title: "Full-stack",
      desc: "Front-of-house + kitchen + inventory + vendors + reports. One product, not five integrations.",
    },
    {
      icon: "🌱",
      title: "Self-improving",
      desc: "HSN/GST suggestions cache and reuse. Cost drops over time as the system learns.",
    },
    {
      icon: "🔁",
      title: "1-click migrate",
      desc: "Switch from Square or Clover by uploading their export. Auto-detect, fuzzy-match, done.",
    },
    {
      icon: "🏪",
      title: "Multi-vertical",
      desc: "Restaurants, bakeries, salons, grocery, clothing. One platform — different defaults.",
    },
  ];

  return (
    <section className="bk-showcase">
      <div className="bk-showcase-bg" />
      <div className="container">
        <motion.div
          className="bk-showcase-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="bk-showcase-eyebrow">★ FLAGSHIP · HDT BOOKING</span>
          <h2 className="bk-showcase-title">
            One number. Your whole restaurant runs on it.
          </h2>
          <p className="bk-showcase-sub">
            A WhatsApp-first operating system for restaurants, bakeries,
            salons, grocery &amp; clothing stores. Watch the 60-second flow.
          </p>
        </motion.div>

        <div className="bk-steps">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              className="bk-step"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
            >
              <div className="bk-step-num">{s.n}</div>
              <div className="bk-step-icon">{s.icon}</div>
              <h4 className="bk-step-title">{s.title}</h4>
              <p className="bk-step-desc">{s.desc}</p>
              {i < steps.length - 1 && (
                <ArrowRight className="bk-step-arrow" size={28} />
              )}
            </motion.div>
          ))}
        </div>

        <div className="bk-tiles">
          {differentiators.map((d, i) => (
            <motion.div
              key={i}
              className="bk-tile"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
            >
              <div className="bk-tile-icon">{d.icon}</div>
              <div>
                <h5 className="bk-tile-title">{d.title}</h5>
                <p className="bk-tile-desc">{d.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="bk-showcase-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <button
            className="bk-cta-primary"
            onClick={() => navigate("/whatsapp-ordering")}
          >
            See full feature list <ArrowRight size={18} />
          </button>
          <button
            className="bk-cta-secondary"
            onClick={() => openExternalLink(InfoService?.bookingLink)}
          >
            Start Free Trial
          </button>
          <button
            className="bk-cta-ghost"
            onClick={() => openExternalLink(InfoService?.whatsappLink)}
          >
            <MessageCircle size={18} /> Talk on WhatsApp
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default BookingShowcase;
